// src/pages/PrivacyPolicyPage.jsx
import React from "react";

const SITE_DOMAIN = "https://mi64.eight-percent.com"; // ← при необходимости замените
const CONTACT_EMAIL = "mi@coolmart.ru";
const LAST_UPDATE = "13.08.2025";

export default function PrivacyPolicyPage() {
  const onPrint = () => window.print();

  return (
    <div id="top" className="min-h-screen bg-neutral-50 text-neutral-900">
      {/* Hero — пастельный градиент, без логотипа */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-100 via-orange-50 to-sky-100" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-neutral-800 sm:text-3xl">
              Политика обработки персональных данных и конфиденциальности
            </h1>
            <p className="mt-1 text-neutral-600">
              Интернет-магазин «Mi64» • Редакция от {LAST_UPDATE}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="/"
              className="inline-flex items-center rounded-md bg-white/80 px-3 py-1.5 text-sm font-medium text-neutral-800 ring-1 ring-neutral-200 shadow-sm hover:bg-white"
            >
              На главную
            </a>
            <button
              onClick={onPrint}
              className="inline-flex items-center rounded-md bg-neutral-800/5 px-3 py-1.5 text-sm font-medium text-neutral-800 ring-1 ring-neutral-300 hover:bg-neutral-800/10"
            >
              Печать / PDF
            </button>
            <a
              className="inline-flex items-center rounded-md bg-white/60 px-3 py-1.5 text-sm font-medium text-amber-800 ring-1 ring-amber-200 hover:bg-white/80"
              href={`mailto:${CONTACT_EMAIL}`}
            >
              Написать на {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </header>

      {/* Content + TOC */}
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-12">
          {/* Sidebar TOC — мягкая подложка */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24 rounded-xl border border-neutral-200 bg-white/70 p-4 shadow-sm backdrop-blur">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Навигация
              </h2>
              <nav className="mt-3 space-y-1.5 text-sm">
                {[
                  ["#p1", "1. Общие положения и термины"],
                  ["#p2", "2. Правовые основания и принципы"],
                  ["#p3", "3. Состав персональных данных"],
                  ["#p4", "4. Цели обработки"],
                  ["#p5", "5. Cookies и технологии"],
                  ["#p6", "6. Передача третьим лицам"],
                  ["#p7", "7. Сроки хранения"],
                  ["#p8", "8. Безопасность данных"],
                  ["#p9", "9. Права Пользователя"],
                  ["#p10", "10. Особые условия"],
                  ["#p11", "11. Ответственность"],
                  ["#p12", "12. Порядок и контакты"],
                  ["#p13", "13. Изменение Политики"],
                  ["#consent", "Текст согласия (в формах)"], // <- без слова «чекбокс»
                ].map(([href, label]) => (
                  <a
                    key={href}
                    href={href}
                    className="block rounded-md px-2 py-1 text-neutral-700 hover:bg-neutral-100"
                  >
                    {label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <article className="lg:col-span-9">
            <Card>
              <Section id="p1" title="1. Общие положения и термины">
                <p>
                  Оператором персональных данных является{" "}
                  <strong>ИП Некрасов Максим Сергеевич</strong>, ИНН{" "}
                  <strong>253600788308</strong>, ОГРН{" "}
                  <strong>304253635000349</strong>, юридический адрес:{" "}
                  <strong>г. Владивосток, ул. Толстого, 42-6</strong>. Контакт
                  по вопросам персональных данных:{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-amber-700 underline hover:no-underline"
                  >
                    {CONTACT_EMAIL}
                  </a>
                  . Далее — «Оператор», «Администрация», «мы».
                </p>
                <p>
                  Настоящая Политика действует в отношении персональных данных
                  пользователей сайта интернет-магазина «Mi64» (далее — «Сайт»)
                  по адресу <strong>{SITE_DOMAIN}</strong>.
                </p>
                <p>
                  Термины («Пользователь», «Персональные данные», «Обработка
                  ПДн», «Cookies», «IP-адрес» и др.) используются в значениях
                  законодательства РФ. Используя Сайт, Пользователь подтверждает
                  согласие с Политикой; при несогласии необходимо прекратить
                  использование Сайта. Политика действует только в отношении
                  Сайта «Mi64» и не распространяется на ресурсы третьих лиц.
                </p>
              </Section>

              <Section id="p2" title="2. Правовые основания и принципы">
                <ul className="list-disc pl-6">
                  <li>согласие Пользователя;</li>
                  <li>
                    заключение и исполнение договора (оформление/доставка
                    заказов, возвраты);
                  </li>
                  <li>исполнение требований законодательства РФ.</li>
                </ul>
                <p className="mt-2">
                  Мы придерживаемся принципов законности, минимизации,
                  ограничения цели и срока хранения, точности, конфиденциальности
                  и безопасности.
                </p>
              </Section>

              <Section id="p3" title="3. Состав персональных данных">
                <p className="font-medium">Предоставляемые Пользователем:</p>
                <ul className="list-disc pl-6">
                  <li>ФИО;</li>
                  <li>телефон;</li>
                  <li>e-mail;</li>
                  <li>адрес(а) доставки;</li>
                  <li>адрес проживания (при необходимости);</li>
                  <li>иные сведения в формах Сайта.</li>
                </ul>
                <p className="mt-3 font-medium">
                  Собираемые автоматически при посещении Сайта:
                </p>
                <ul className="list-disc pl-6">
                  <li>
                    IP-адрес, файлы cookies, данные localStorage/SessionStorage;
                  </li>
                  <li>сведения о браузере и устройстве;</li>
                  <li>реферер (источник перехода), дата и время визитов;</li>
                  <li>технические параметры сессии.</li>
                </ul>
                <p className="mt-3">
                  <strong>Онлайн-оплата на Сайте не осуществляется.</strong> Мы
                  не собираем и не храним реквизиты банковских карт. При
                  офлайн-расчётах (при получении, наложенный платёж, по счёту)
                  обрабатываются только факт расчёта, сумма, дата, способ оплаты,
                  номер чека/накладной — без карточных реквизитов.
                </p>
                <p className="mt-3">
                  Отключение cookies может ограничить работу разделов,
                  требующих авторизации.
                </p>
              </Section>

              <Section id="p4" title="4. Цели обработки">
                <ul className="list-disc pl-6">
                  <li>регистрация, идентификация, личный кабинет;</li>
                  <li>оформление и исполнение заказов, доставка, сервис;</li>
                  <li>обработка обращений, клиентская/техническая поддержка;</li>
                  <li>
                    учёт офлайн-расчётов и возвратов, исполнение требований
                    бухгалтерского и налогового учёта;
                  </li>
                  <li>сервисные уведомления о статусе заказов;</li>
                  <li>аналитика использования Сайта и его улучшение;</li>
                  <li>маркетинговые сообщения — только при отдельном согласии.</li>
                </ul>
              </Section>

              <Section id="p5" title="5. Cookies и аналогичные технологии">
                <p>
                  Используем строго необходимые, функциональные и аналитические
                  cookies, а также хранилища браузера для работы авторизации и
                  личного кабинета. Пользователь может ограничить cookies в
                  настройках браузера; это может ухудшить работу некоторых
                  функций.
                </p>
              </Section>

              <Section id="p6" title="6. Передача персональных данных третьим лицам">
                <p>
                  Передаём персональные данные только в объёме, необходимом для
                  исполнения заказов и требований закона:
                </p>
                <ul className="list-disc pl-6">
                  <li>
                    <strong>курьерским и почтовым службам</strong> — для
                    доставки (ФИО, телефон, адрес доставки, состав/номер
                    заказа, сумма к оплате при необходимости);
                  </li>
                  <li>
                    <strong>уполномоченным государственным органам</strong> —
                    на основании и в порядке, предусмотренных законодательством
                    РФ.
                  </li>
                </ul>
                <p className="mt-3">
                  Мы не продаём и не обмениваем персональные данные. Онлайн-оплата
                  не используется; карточные реквизиты нами не обрабатываются. При
                  наложенном платеже расчёт выполняет почтовый/курьерский оператор.
                </p>
                <p className="mt-3">
                  Трансграничная передача ПДн не осуществляется, кроме случаев
                  доставки в иные государства; при необходимости оформляется
                  отдельное согласие и обеспечивается надлежащая защита прав
                  субъектов ПДн.
                </p>
              </Section>

              <Section id="p7" title="7. Сроки хранения">
                <p>
                  Персональные данные хранятся не дольше, чем необходимо для
                  целей обработки, либо в сроки, установленные законодательством
                  РФ (включая налоговый и бухгалтерский учёт). Аккаунт и
                  связанные данные могут быть удалены по обращению Пользователя,
                  если иное не требуется по закону.
                </p>
              </Section>

              <Section id="p8" title="8. Безопасность данных">
                <p>
                  Применяем организационные и технические меры защиты: доступ по
                  ролям, хранение паролей в виде криптографических хэш-сумм,
                  защищённые соединения (HTTPS/TLS), резервное копирование,
                  журналы безопасности и аудит действий, ограничение
                  административного доступа. При инцидентах безопасности
                  информируем Пользователей, когда это требуется законом, и
                  минимизируем риски.
                </p>
              </Section>

              <Section id="p9" title="9. Права Пользователя">
                <ul className="list-disc pl-6">
                  <li>получать информацию об обработке своих ПДн;</li>
                  <li>требовать уточнения, блокирования или удаления ПДн;</li>
                  <li>отзывать согласие, ограничивать обработку, возражать;</li>
                  <li>обращаться с жалобой в Роскомнадзор.</li>
                </ul>
                <p className="mt-3">
                  Обращения направляйте на{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-amber-700 underline hover:no-underline"
                  >
                    {CONTACT_EMAIL}
                  </a>{" "}
                  или через личный кабинет/форму обратной связи на Сайте.
                </p>
              </Section>

              <Section id="p10" title="10. Особые условия">
                <ul className="list-disc pl-6">
                  <li>
                    данные несовершеннолетних — при участии законных
                    представителей в случаях, предусмотренных законом;
                  </li>
                  <li>
                    автоматизированные решения, существенно влияющие на права и
                    свободы (включая профилирование), без отдельного уведомления
                    и согласия не применяются;
                  </li>
                  <li>
                    Оператор исходит из корректности данных, предоставленных
                    Пользователем.
                  </li>
                </ul>
              </Section>

              <Section id="p11" title="11. Ответственность">
                <p>
                  За нарушение установленного режима обработки ПДн Оператор несёт
                  ответственность в соответствии с законодательством РФ. Оператор
                  не отвечает за раскрытие ПДн, если они стали публичными до
                  раскрытия, были получены от третьих лиц до их получения
                  Оператором, раскрыты с согласия Пользователя либо по законному
                  требованию уполномоченных органов.
                </p>
              </Section>

              <Section id="p12" title="12. Порядок реализации прав и контакты">
                <p>
                  Запросы по персональным данным направляйте на{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-amber-700 underline hover:no-underline"
                  >
                    {CONTACT_EMAIL}
                  </a>{" "}
                  с темой «Запрос по персональным данным» или через форму в личном
                  кабинете. Срок рассмотрения — до 30 календарных дней.
                </p>
                <div className="mt-3 rounded-md bg-amber-50 p-4 text-sm ring-1 ring-amber-100">
                  <p>
                    Оператор: <strong>ИП Некрасов Максим Сергеевич</strong>
                    <br />
                    ИНН: <strong>253600788308</strong> • ОГРН:{" "}
                    <strong>304253635000349</strong>
                    <br />
                    Адрес: <strong>г. Владивосток, ул. Толстого, 42-6</strong>
                    <br />
                    Сайт: <strong>{SITE_DOMAIN}</strong>
                    <br />
                    E-mail:{" "}
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      className="text-amber-700 underline hover:no-underline"
                    >
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                </div>
              </Section>

              <Section id="p13" title="13. Изменение Политики">
                <p>
                  Мы вправе обновлять Политику без предварительного уведомления.
                  Новая редакция вступает в силу с момента публикации на Сайте по
                  адресу <strong>{SITE_DOMAIN}/policy/privacy</strong>, если
                  иное не предусмотрено новой редакцией.
                </p>
              </Section>

              <Section id="consent" title="Текст согласия (в формах)">
                <p className="italic">
                  Нажимая кнопку «Зарегистрироваться/Оформить заказ» и/или
                  устанавливая флажок «Согласен(а) с обработкой персональных
                  данных», я свободно, своей волей и в своём интересе даю{" "}
                  <strong>ИП Некрасову Максиму Сергеевичу</strong> (ИНН
                  253600788308, ОГРН 304253635000349, адрес: г. Владивосток, ул.
                  Толстого, 42-6) согласие на обработку моих персональных данных
                  (ФИО, телефон, e-mail, адрес(а) доставки и иные указанные мной
                  данные) в целях регистрации на Сайте, оформления и исполнения
                  заказа, коммуникаций по заказу и сервисной поддержки, а также
                  для получения маркетинговых сообщений при моём отдельном
                  согласии. Онлайн-оплата на Сайте не используется; реквизиты
                  банковских карт не собираются и не хранятся. Срок обработки —
                  до достижения целей обработки либо до моего отзыва. Отозвать
                  согласие можно по адресу {CONTACT_EMAIL} или через личный
                  кабинет. С Политикой конфиденциальности ознакомлен(а).
                </p>
              </Section>

              <div className="mt-10 flex justify-end">
                <a href="#top" className="text-sm text-neutral-600 underline">
                  ↑ Наверх
                </a>
              </div>
            </Card>
          </article>
        </div>
      </main>
    </div>
  );
}

/* ————— UI helpers ————— */

function Card({ children }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
      {children}
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="mt-8 text-xl font-semibold tracking-tight text-neutral-900 first:mt-0">
        {title}
      </h2>
      <div className="mt-3 space-y-3 leading-relaxed text-neutral-800">
        {children}
      </div>
      <hr className="my-6 border-neutral-200" />
    </section>
  );
}
